/**
 * Postcode / address lookup (Loqate) — Alpine component.
 *
 * Exposes registerPostcodeLookup(Alpine), called from app.ts BEFORE
 * Alpine.start() so the `postcodeLookup` component exists before Alpine walks
 * the DOM. All per-instance setup happens inside the component factory.
 *
 * The Loqate library itself is a UMD loaded as a classic <script> on the
 * address page (via config('lottery.address_lookup_js')) — bundling the UMD
 * makes its global-vs-exports branch ambiguous under Vite dev, so we read the
 * global it sets. The library is instantiated lazily on first lookup, not at
 * component mount, so it doesn't matter whether the classic script runs before
 * or after Alpine.start() — by the time a user searches, it has loaded. The API
 * key comes from `data-loqate-key` on #postcode-lookup, falling back to the
 * site default so no shared-view edit is required.
 */

const DEFAULT_LOQATE_KEY = 'TZ69-EW94-MH91-CM99';

interface LoqateAddress {
    Line1: string;
    Line2: string;
    Line3: string;
    City: string;
    Province: string;
    PostalCode: string;
    Company: string;
}

interface LoqateResponse {
    Items?: Array<{ Id: string; Type: string; Text: string; Description: string }>;
}

interface LoqateService {
    getAddressesByPostcode(postcode: string): Promise<LoqateResponse>;
    getAddressesByContainerId(id: string): Promise<LoqateResponse>;
    getAddressById(id: string): Promise<LoqateAddress | undefined>;
}

interface LoqateConstructor {
    new (options: { Key: string }): LoqateService;
}

declare global {
    interface Window {
        LoqatePostcodeAddressLookup?: LoqateConstructor;
    }
}

interface AddressResult {
    id: string;
    type: string;
    text: string;
    desc: string;
    label: string;
}

// Minimal shape of the Alpine global we use (avoids depending on alpinejs' own
// exported type name across versions).
interface AlpineLike {
    data(name: string, callback: () => unknown): void;
}

export default function registerPostcodeLookup(Alpine: AlpineLike): void {
    Alpine.data('postcodeLookup', () => {
        const root = document.getElementById('postcode-lookup');

        // User-facing copy comes from data-msg-* attributes on #postcode-lookup
        // (server-rendered from Site Values so it can be translated); the English
        // literals are the fallback if an attribute is ever missing.
        const ds = root?.dataset ?? {};
        const messages = {
            postcodeInvalid: ds.msgPostcodeInvalid || 'Please enter a valid postcode',
            lookupUnavailable: ds.msgAddressLookupUnavailable || 'Address lookup is currently unavailable',
            noAddressesFound: ds.msgNoAddressesFound || 'No addresses found',
            lookupError: ds.msgAddressLookupError || 'There was an error retrieving addresses for this postcode',
            selectError: ds.msgAddressSelectError || 'There was an error retrieving the selected address',
        };

        // Manual address fields populated once an address is chosen.
        const manualFields = {
            address_1: document.getElementById('address_1') as HTMLInputElement | null,
            address_2: document.getElementById('address_2') as HTMLInputElement | null,
            address_3: document.getElementById('address_3') as HTMLInputElement | null,
            town: document.getElementById('town') as HTMLInputElement | null,
            county: document.getElementById('county') as HTMLInputElement | null,
            postcode: document.getElementById('postcode') as HTMLInputElement | null,
        };
        const addressVisibilityCheckbox = document.getElementById('toggle-address-fields-visibility') as HTMLInputElement | null;
        const addressManualFields = document.getElementById('address-manual');
        const csrfToken = (document.querySelector('input[type=hidden][name=_token]') as HTMLInputElement | null)?.value ?? '';

        // Lazily built on first lookup — see the file header for why.
        let service: LoqateService | null = null;
        const getService = (): LoqateService | null => {
            if (service) {
                return service;
            }
            const Loqate = window.LoqatePostcodeAddressLookup;
            if (!Loqate) {
                return null;
            }
            service = new Loqate({ Key: root?.dataset.loqateKey || DEFAULT_LOQATE_KEY });
            return service;
        };

        return {
            errorMessage: null as string | null,
            addresses: [] as AddressResult[],
            isLoading: false,
            showButtons: true,

            hasItemsPropertyWithItems(response: LoqateResponse): boolean {
                return Object.prototype.hasOwnProperty.call(response, 'Items')
                    && (response.Items?.length ?? 0) > 0;
            },

            populateResultsBox(items: Array<{ Id: string; Type: string; Text: string; Description: string }>): void {
                this.addresses = [];

                items.sort((a, b) => a.Text.localeCompare(b.Text, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }));

                items.forEach((item) => {
                    this.addresses.push({
                        id: item.Id,
                        type: item.Type,
                        text: item.Text,
                        desc: item.Description,
                        label: item.Text.concat(' - ', item.Description).trim(),
                    });
                });
            },

            findAddresses(): void {
                // $refs is injected by Alpine at runtime, not part of this literal.
                const refs = (this as unknown as { $refs: { postcodeInput: HTMLInputElement } }).$refs;
                const postcode = refs.postcodeInput.value.trim();

                if (postcode === '' || postcode.length < 4) {
                    this.errorMessage = messages.postcodeInvalid;
                    return;
                }

                const svc = getService();
                if (!svc) {
                    this.errorMessage = messages.lookupUnavailable;
                    return;
                }

                this.isLoading = true;

                svc.getAddressesByPostcode(postcode).then((response) => {
                    this.addresses = [];
                    this.errorMessage = null;
                    this.isLoading = false;

                    if (!this.hasItemsPropertyWithItems(response)) {
                        this.errorMessage = messages.noAddressesFound;
                        this.logError('No addresses found for postcode input', { postcode });
                        return;
                    }

                    this.populateResultsBox(response.Items!);
                }).catch((error: Error) => {
                    this.errorMessage = messages.lookupError;
                    this.logError(error.message, { postcode });
                    this.isLoading = false;
                });
            },

            findAddress(id: string, type: string): void {
                const svc = getService();
                if (!svc) {
                    this.errorMessage = messages.lookupUnavailable;
                    return;
                }

                // A "Container" result (Postcode, Street, Locality, …) drills down to
                // the addresses it contains; an "Address" result is the final pick.
                if (type !== 'Address') {
                    svc.getAddressesByContainerId(id).then((response) => {
                        if (!this.hasItemsPropertyWithItems(response)) {
                            this.errorMessage = messages.noAddressesFound;
                            this.logError('No addresses found for container', { id, type });
                            return;
                        }

                        this.populateResultsBox(response.Items!);
                    }).catch((error: Error) => {
                        this.errorMessage = messages.selectError;
                        this.logError(error.message, { id, type });
                    });

                    return;
                }

                svc.getAddressById(id).then((address) => {
                    this.addresses = [];
                    this.isLoading = true;

                    if (!address) {
                        this.isLoading = false;
                        return;
                    }

                    this.resetManualAddressFields();
                    this.showManualAddressFields();

                    let line1 = address.Line1;
                    if (address.Company !== '') {
                        line1 = address.Company.concat(', ', line1);
                    }

                    if (manualFields.address_1) manualFields.address_1.value = line1;
                    if (manualFields.address_2) manualFields.address_2.value = address.Line2;
                    if (manualFields.address_3) manualFields.address_3.value = address.Line3;
                    if (manualFields.town) manualFields.town.value = address.City;
                    if (manualFields.county) manualFields.county.value = address.Province;
                    if (manualFields.postcode) manualFields.postcode.value = address.PostalCode;

                    this.isLoading = false;
                }).catch((error: Error) => {
                    this.errorMessage = messages.selectError;
                    this.logError(error.message, { addressId: id });
                    this.isLoading = false;
                });
            },

            showManualAddressFields(): void {
                if (addressVisibilityCheckbox) addressVisibilityCheckbox.checked = true;
                if (addressManualFields) addressManualFields.style.display = 'block';
                this.showButtons = false;
            },

            resetManualAddressFields(): void {
                Object.values(manualFields).forEach((field) => {
                    if (field) field.value = '';
                });
            },

            logError(error: string, context: Record<string, unknown> = {}): void {
                fetch('/log_postcode_lookup_error', {
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ _token: csrfToken, error, context }),
                }).catch((err) => {
                    console.error(err);
                });
            },
        };
    });
}
